import { useMemo } from 'react';

// auth hook
import useUserRole from './useUserRole';
import useMenuItems from 'menu-items';
import useEnums from './useEnum';
import useSkipCompanyQuery from './useSkipCompanyQuery';

// types
import { NavItemType } from 'types/menu';

// store
import { useGetCompanyQuery } from 'store/reducers/api/company';

const useFilteredMenuItems = () => {
  const menuItems = useMenuItems();
  const { UserRolesEnum } = useEnums();
  const { hasSystemRole, activeRole, hasCompanyRole, hasPropertyRole } = useUserRole();
  const skipCompanyQuery = useSkipCompanyQuery(activeRole);
  const { data: userCompany } = useGetCompanyQuery(activeRole?.company_id!, { skip: skipCompanyQuery });

  return useMemo(() => {
    if (hasSystemRole) {
      return { ...menuItems };
    }

    if (hasCompanyRole) {
      const companyRoleItems = ['companies', 'users', 'dashboard', 'gateways', 'settings'];
      if (activeRole?.user_role_type_id === UserRolesEnum.COMPANY_ADMIN && userCompany?.allow_assignment) {
        companyRoleItems.push('micrometers', 'assignment');
      }

      const filteredItems = filterMenuItems(menuItems.items[0].children || [], companyRoleItems);

      return {
        ...menuItems,
        items: [{ ...menuItems.items[0], children: filteredItems }]
      };
    }

    if (hasPropertyRole) {
      const propertyRoleItems = ['companies', 'users', 'dashboard', 'gateways', 'settings'];
      const filteredItems = filterMenuItems(menuItems.items[0].children || [], propertyRoleItems);
      return {
        ...menuItems,
        items: [{ ...menuItems.items[0], children: filteredItems }]
      };
    }

    if (activeRole?.user_role_type_id === UserRolesEnum.NOTIFICATION_CONTACT) {
      const filteredItems = filterMenuItems(menuItems.items[0].children || [], ['settings']);
      return {
        ...menuItems,
        items: [{ ...menuItems.items[0], children: filteredItems }]
      };
    }

    return { items: [] };
  }, [hasSystemRole, hasCompanyRole, menuItems, activeRole, UserRolesEnum, userCompany, hasPropertyRole]);
};

export default useFilteredMenuItems;

const filterMenuItems = (items: NavItemType[], allowedItems: string[]): NavItemType[] => {
  return items
    .filter((item) => item.id && allowedItems.includes(item.id)) // Filter out items based on allowedItems array
    .map((item) => {
      if (item.children) {
        // Recursively filter children
        return {
          ...item,
          children: filterMenuItems(item.children, allowedItems) // Recursive call for children
        };
      }
      return item; // If no children, return the item
    });
};
